// #766 Story E View 5 — disengaged-call view (anti-survivorship).
//
// Pre-filtered table of calls where ANY of:
//   - Avg engagement_score across played cards < 40
//   - Mood at end is `low` or `distressed`
//   - Call duration < 60s AND end_reason NOT user-initiated
//
// Each row links through to View 1 (/admin/conversations/<id>) for
// the full dealer-hand diagnostic.
//
// Decision the view informs: what dealer strategies are failing?
// Most analytics dashboards study wins; failure modes teach more.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { userSubPageStyles, adminFetch } from './userSubPageStyles';

const VERSION = 'v1.0.0';

const RANGE_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];

function fmtDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function fmtSeconds(s) {
  if (s == null) return '—';
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return r === 0 ? `${m}m` : `${m}m ${r}s`;
}

function moodBadge(mood) {
  if (!mood) return <span style={{ color: '#aaa' }}>—</span>;
  const palette = {
    cheerful:   { bg: '#E8F5E9', fg: '#2E7D32' },
    neutral:    { bg: '#F5F5F5', fg: '#666' },
    quiet:      { bg: '#E3F2FD', fg: '#1565C0' },
    low:        { bg: '#FFF3E0', fg: '#E65100' },
    distressed: { bg: '#FFEBEE', fg: '#C62828' },
  };
  const c = palette[mood] || { bg: '#EEE', fg: '#666' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 10,
      fontSize: '0.72rem', fontWeight: 600,
      background: c.bg, color: c.fg, textTransform: 'capitalize',
    }}>{mood}</span>
  );
}

function engagementCell(avg) {
  if (avg == null) return <span style={{ color: '#aaa' }}>—</span>;
  const v = Math.round(avg);
  let color = '#666';
  if (avg < 30) color = '#DC3545';
  else if (avg < 60) color = '#E8845A';
  else color = '#28A745';
  return <span style={{ fontWeight: 700, color }}>{v}</span>;
}

export default function AdminConversationsDisengaged() {
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await adminFetch(
          `/api/admin/conversations/disengaged?days=${days}`
        );
        if (cancelled) return;
        setRows(r?.rows || []);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  return (
    <AdminLayout title="Disengaged calls">
      <style>{userSubPageStyles}</style>

      <div className="page-header">
        <div className="page-title">
          Disengaged calls<span className="ver">{VERSION}</span>
        </div>
        <div className="page-subtitle">
          Pre-filtered: avg engagement &lt; 40 · mood ended low/distressed ·
          duration &lt; 60s with non-user-initiated end · #766 Story E View 5
        </div>
      </div>

      <div style={{ marginBottom: 18, display: 'flex', gap: 12, alignItems: 'center' }}>
        <label style={{ fontSize: '0.82rem', color: '#666', fontWeight: 600 }}>Range:</label>
        <select value={days} onChange={e => setDays(parseInt(e.target.value, 10))}
                style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: 4 }}>
          {RANGE_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {rows && <span style={{ color: '#666', fontSize: '0.85rem' }}>
          {rows.length} disengaged call{rows.length === 1 ? '' : 's'} in range
        </span>}
      </div>

      {loading && <p className="loading-state">Loading…</p>}
      {error && <p className="error-state">Error: {error}</p>}

      {!loading && !error && rows && rows.length === 0 && (
        <div className="empty-state">No disengaged calls in this range — that's a good sign.</div>
      )}

      {!loading && !error && rows && rows.length > 0 && (
        <div className="data-grid">
          <table className="data-table">
            <thead>
              <tr>
                <th>When</th>
                <th>User</th>
                <th>Language</th>
                <th>Duration</th>
                <th>Mood end</th>
                <th>End reason</th>
                <th>Avg engagement</th>
                <th>Hand outcome</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.conversation_id}>
                  <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                    {fmtDateTime(r.started_at)}
                  </td>
                  <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.78rem', color: '#888' }}>
                    {(r.user_id || '').slice(0, 8)}…
                  </td>
                  <td>{r.language || '—'}</td>
                  <td>{fmtSeconds(r.duration_s)}</td>
                  <td>{moodBadge(r.mood_at_end)}</td>
                  <td style={{ color: '#888', fontSize: '0.82rem' }}>{r.end_reason || '—'}</td>
                  <td>{engagementCell(r.avg_engagement)}</td>
                  <td style={{ color: '#666', fontSize: '0.85rem' }}>
                    {r.n_played}/{r.n_dealt} played
                    {r.n_declined > 0 && (
                      <span style={{ color: '#C62828' }}>, {r.n_declined} declined</span>
                    )}
                  </td>
                  <td>
                    <Link to={`/admin/conversations/${r.conversation_id}`}
                          style={{ color: '#E8845A', fontSize: '0.85rem' }}>
                      diagnose →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && rows && rows.length >= 200 && (
        <p style={{ marginTop: 14, color: '#aaa', fontSize: '0.82rem', fontStyle: 'italic' }}>
          Result capped at 200 rows — narrow the range if you need to see more.
        </p>
      )}
    </AdminLayout>
  );
}
